import { FC, useCallback, useMemo, useState } from 'react';
import {
  CompanyIcon,
  CopyBlueRectangleIcon,
  DirectionsIcon,
  EditIconBlue,
  MailIcon,
  MapIcon,
  PhoneIcon,
  ServiceAreaIcon
} from 'assets/icons';
import { AssignableMember } from '../jobs.model';
import { getAddressAsString } from '../utils';
import { copyToClipBoard, getInitials } from 'utils/commonUtils';
import { CustomAvatar, If } from 'components';
import { AddressField } from 'containers/invoices/invoices.model';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeamJobPermissions, selectUserDetails, showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useEditJobAssigneesMutation, useEditJobServiceAreaMutation } from '../api';
import EditJobServiceAreaModal from '../job-details/components/EditJobServiceAreaModal';
import ServiceAreaCreateModal from '../create-edit-job/components/ServiceAreaCreateModal';
import { useCreateServiceAreaMutation, useLazyGetServiceAreaByIdQuery } from 'containers/settings/service-areas/api';
import { ServiceAreaType } from 'containers/settings/service-areas/constants';
import { Job } from '../jobs.model';
import ServiceAreaAssigneesModal from '../job-details/components/ServiceAreaAssigneesModal';
import { CreateServiceAreaFormData } from 'containers/settings/service-areas/serviceAreas.model';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';

interface CustomerDetailSectionProps {
  onCustomerClick?: () => void;
  onEditAddress?: (address: AddressField, type: ADDRESS_TYPE) => void;
  canEditJobs: boolean;
  job?: Job;
  serviceAreas?: any[];
  onRefreshServiceAreas?: () => void;
  refetchJob: () => void;
}

interface ServiceAreaState {
  isEditModalOpen: boolean;
  isAddModalOpen: boolean;
  isAssignableMembersModalOpen: boolean;
  selectedServiceAreaId: string | null;
  selectedServiceAreaName: string | null;
  selectedAssignees: AssignableMember[];
  assigneeList: AssignableMember[];
}

const CustomerDetailSection: FC<CustomerDetailSectionProps> = ({
  onCustomerClick,
  onEditAddress,
  canEditJobs,
  job,
  serviceAreas = [],
  onRefreshServiceAreas,
  refetchJob
}) => {
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { isActiveFSMPlusPlan } = useFSMPlanInfo();
  const currentUser = useAppSelector(selectUserDetails);
  const { manageOthersJobs } = useAppSelector(selectCurrentTeamJobPermissions);
  const [serviceAreaState, setServiceAreaState] = useState<ServiceAreaState>({
    isEditModalOpen: false,
    isAddModalOpen: false,
    isAssignableMembersModalOpen: false,
    selectedServiceAreaId: null,
    selectedServiceAreaName: null,
    selectedAssignees: [],
    assigneeList: []
  });

  const [createServiceArea, { isLoading: isLoadingCreateServiceArea }] = useCreateServiceAreaMutation();
  const [editJobAssignees, { isLoading: isLoadingEditJobAssignees }] = useEditJobAssigneesMutation();
  const [editJobServiceArea, { isLoading: isLoadingEditJobServiceArea }] = useEditJobServiceAreaMutation();
  const [getServiceAreaById, { isFetching: isLoadingGetServiceAreaById }] = useLazyGetServiceAreaByIdQuery();

  const { customerLatest: customer, jobServiceArea, jobNumber, id: jobId } = job || {};

  const onCopyAddress = (address: AddressField) => {
    const copied = copyToClipBoard(getAddressAsString(address));
    if (copied) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Address copied to clipboard',
            isMessageHtml: false
          },
          type: NotifierTypes.SUCCESS,
          showClose: true,
          fontStyle: 'text-primary font-normal'
        })
      );
    }
  };

  const openGoogleMaps = (address: AddressField) => {
    const addressString = getAddressAsString(address);
    if (addressString) {
      const encodedAddress = encodeURIComponent(addressString);
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  const handleServiceAreaSelect = useCallback(
    async (serviceAreaId: string) => {
      if (!currentTeam?.id) return;

      try {
        setServiceAreaState(prev => ({
          ...prev,
          isEditModalOpen: false,
          isAssignableMembersModalOpen: true,
          selectedServiceAreaId: serviceAreaId
        }));

        const data = await getServiceAreaById({
          teamId: currentTeam.id.toString(),
          serviceAreaId: serviceAreaId
        }).unwrap();

        if (data) {
          const assignees =
            data.assignees?.map(assignee => ({
              merchantId: assignee.id,
              name: assignee.name,
              email: assignee.email || '',
              phone: assignee.phone || '',
              profilePictureUrl: assignee.profilePictureURL || '',
              id: assignee.id,
              colorCode: ''
            })) || [];

          const selectedAssignees = assignees.filter(assignee => job?.assignees?.some(a => a.id === assignee.id)) || [];
          let assignableMembersList = assignees;
          if (!manageOthersJobs) {
            assignableMembersList = assignees.filter(assignee => assignee.merchantId === currentUser?.id);
          }
          setServiceAreaState(prev => ({
            ...prev,
            selectedServiceAreaName: data.name,
            selectedAssignees,
            assigneeList: assignableMembersList
          }));
        }
      } catch (error) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Failed to fetch service area details',
              isMessageHtml: false
            },
            type: NotifierTypes.ERROR,
            showClose: true
          })
        );
        setServiceAreaState(prev => ({
          ...prev,
          isAssignableMembersModalOpen: false
        }));
      }
    },
    [currentTeam?.id, getServiceAreaById, job?.assignees, dispatch]
  );

  const handleAssigneesAndServiceAreaSave = useCallback(
    async (members: AssignableMember[]) => {
      if (!currentTeam?.id || !jobId) return;

      try {
        if (members?.length >= 0) {
          await editJobAssignees({
            teamId: currentTeam.id,
            jobId: jobId,
            assignees: members?.map(member => member.merchantId)
          }).unwrap();
        }
        await editJobServiceArea({
          teamId: currentTeam.id,
          jobId: jobId,
          serviceAreaId: serviceAreaState.selectedServiceAreaId
        }).unwrap();
        refetchJob();
        setServiceAreaState({
          isEditModalOpen: false,
          isAddModalOpen: false,
          isAssignableMembersModalOpen: false,
          selectedServiceAreaId: null,
          selectedServiceAreaName: null,
          selectedAssignees: [],
          assigneeList: []
        });
      } catch (error) {
        // Error handling is already handled by the API mutation
      }
    },
    [currentTeam?.id, jobId, serviceAreaState.selectedServiceAreaId, editJobAssignees, editJobServiceArea]
  );

  const handleCreateServiceArea = useCallback(
    async (data: CreateServiceAreaFormData) => {
      if (!currentTeam?.id) return;

      try {
        const payload = {
          teamId: currentTeam.id.toString(),
          name: data.name,
          colorCode: data.color,
          type: data.shape.type,
          assignees: data.members.map(member => member.merchantId),
          ...(data.shape.type === ServiceAreaType.POLYGON
            ? { vertices: data.shape.data as [number, number][] }
            : {
                centroid: (data.shape.data as { centroid: [number, number]; range: number }).centroid,
                range: (data.shape.data as { centroid: [number, number]; range: number }).range
              })
        };

        await createServiceArea(payload).unwrap();
        setServiceAreaState(prev => ({ ...prev, isAddModalOpen: false }));
        onRefreshServiceAreas?.();
      } catch (error) {
        // Error handling is already handled by the API mutation
      }
    },
    [currentTeam?.id, createServiceArea, onRefreshServiceAreas]
  );

  const handleServiceAreaAssigneeModalClose = () => {
    setServiceAreaState({
      isEditModalOpen: false,
      isAddModalOpen: false,
      isAssignableMembersModalOpen: false,
      selectedServiceAreaId: null,
      selectedServiceAreaName: null,
      selectedAssignees: [],
      assigneeList: []
    });
  };

  const handleServiceAreaAssigneeModalBack = () => {
    setServiceAreaState(prev => ({
      ...prev,
      isAssignableMembersModalOpen: false,
      isEditModalOpen: true,
      selectedServiceAreaName: null,
      selectedAssignees: [],
      assigneeList: []
    }));
  };

  const customerInitials = useMemo(() => getInitials(customer?.name || ''), [customer?.name]);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[17px] font-semibold text-primaryText">Customer details</h2>
        <div className="cursor-pointer text-sbase font-semibold text-secondary" onClick={onCustomerClick}>
          View customer details
        </div>
      </div>

      <div className="mb-6 flex items-center">
        <CustomAvatar
          src={''}
          height="h-[55px]"
          width="w-[55px]"
          className="text-xl text-primaryText"
          label={customerInitials}>
          {customerInitials}
        </CustomAvatar>
        <div className="ml-3 flex flex-col gap-1">
          <div className="text-17px font-bold text-gray-800">
            {`${customer?.name || ''} ${customer?.lastName || ''}`}
          </div>
          <div className="flex items-center text-sbase text-accentText">
            <PhoneIcon className="mr-2 h-[14px] w-[14px]" />
            <span>{customer.phoneNumber || '-'}</span>
          </div>
          <div className="flex items-center gap-2">
            <MailIcon className="path path-fill-current h-[14px] w-[14px] text-accentText" />
            <div className="text-sbase text-accentText">{customer.email || '-'}</div>
          </div>
        </div>
      </div>

      {customer.serviceAddress?.length > 0 && (
        <div className="flex flex-wrap gap-9 border-t border-borderGray pt-4">
          <div className="flex w-[370px] items-start">
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <CompanyIcon className="path-fill-current path h-[18px] w-[18px] text-accentText" />
                <div className="mb-1 text-sbase font-medium text-primaryText">Service address</div>
                <If condition={canEditJobs}>
                  <EditIconBlue
                    className="h-[18px] w-[18px] cursor-pointer"
                    onClick={() => onEditAddress(customer.serviceAddress[0], ADDRESS_TYPE.SERVICE)}
                  />
                </If>
              </div>
              <div className="mt-[6px] flex flex-col items-start">
                <div className="flex">
                  <div id="service-address" className="w-[275px] break-words text-sbase text-primaryText">
                    {getAddressAsString(customer.serviceAddress[0])}
                  </div>
                  <div
                    className="ml-2 inline-flex h-[24px] w-[24px] cursor-pointer items-center justify-center
                     rounded-full bg-secondaryBtn hover:bg-gray-200">
                    <CopyBlueRectangleIcon
                      className="h-[14px] w-[14px] cursor-pointer"
                      onClick={() => onCopyAddress(customer.serviceAddress[0])}
                    />
                  </div>
                  <div
                    className="ml-3 inline-flex h-[24px] w-[24px] cursor-pointer items-center
                     justify-center rounded-full bg-secondaryBtn hover:bg-gray-200"
                    onClick={() => openGoogleMaps(customer.serviceAddress[0])}>
                    <DirectionsIcon className="h-[14px] w-[14px]" />
                  </div>
                </div>
                {customer.serviceAddress[0].addressNote && (
                  <div className="mt-2 pr-24 text-sm text-accentText">{customer.serviceAddress[0].addressNote}</div>
                )}
              </div>
            </div>
          </div>

          <If condition={Boolean(customer?.billingAddress)}>
            <div className="flex items-start">
              <div className="flex-1">
                <div className="flex items-start gap-2">
                  <MapIcon className="h-[18px] w-[18px] text-accentText" />
                  <div className="mb-1 text-sbase font-medium text-primaryText">Billing address</div>
                </div>
                <div className="mt-[6px] flex items-start">
                  <div className="flex text-accentText">
                    <div id="billing-address" className="w-[275px] break-words text-primaryText">
                      {getAddressAsString(customer?.billingAddress)}
                    </div>
                    <div
                      className="ml-2 inline-flex h-[24px] w-[24px] cursor-pointer items-center justify-center
                   rounded-full bg-secondaryBtn hover:bg-gray-200">
                      <CopyBlueRectangleIcon
                        className="h-[14px] w-[14px] cursor-pointer"
                        onClick={() => onCopyAddress(customer?.billingAddress)}
                      />
                    </div>
                    <div
                      className="ml-3 inline-flex h-[24px] w-[24px] cursor-pointer items-center
                     justify-center rounded-full bg-secondaryBtn hover:bg-gray-200"
                      onClick={() => openGoogleMaps(customer?.billingAddress)}>
                      <DirectionsIcon className="h-[14px] w-[14px]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </If>
        </div>
      )}
      <If condition={isActiveFSMPlusPlan}>
        <div className="flex w-[370px] items-start pt-5">
          <div className="flex-1">
            <div className="flex items-start gap-2">
              <ServiceAreaIcon className="h-[18px] w-[18px]" />
              <div className="mb-1 text-sbase font-medium text-primaryText">Service area</div>
              <If condition={canEditJobs}>
                <EditIconBlue
                  className="h-[18px] w-[18px] cursor-pointer"
                  onClick={() =>
                    setServiceAreaState(prev => ({
                      ...prev,
                      isEditModalOpen: true
                    }))
                  }
                />
              </If>
            </div>
            <div className="text-sbase font-medium text-primaryText">
              {jobServiceArea?.id ? jobServiceArea?.name : '-'}
            </div>
          </div>
        </div>
      </If>
      <EditJobServiceAreaModal
        isOpen={serviceAreaState.isEditModalOpen}
        onClose={() => setServiceAreaState(prev => ({ ...prev, isEditModalOpen: false }))}
        onSelect={handleServiceAreaSelect}
        selectedAreaId={jobServiceArea?.id || ''}
        jobNumber={jobNumber || ''}
        serviceAreas={serviceAreas}
        onCreateClick={() => {
          setServiceAreaState(prev => ({ ...prev, isEditModalOpen: false, isAddModalOpen: true }));
        }}
      />
      <ServiceAreaCreateModal
        isOpen={serviceAreaState.isAddModalOpen}
        onClose={() => setServiceAreaState(prev => ({ ...prev, isAddModalOpen: false }))}
        onSave={handleCreateServiceArea}
        isLoading={isLoadingCreateServiceArea}
      />
      {serviceAreaState.isAssignableMembersModalOpen && (
        <ServiceAreaAssigneesModal
          onClose={handleServiceAreaAssigneeModalClose}
          list={serviceAreaState.assigneeList}
          selectedMembers={serviceAreaState.selectedAssignees}
          onSave={handleAssigneesAndServiceAreaSave}
          isSaving={isLoadingEditJobAssignees || isLoadingEditJobServiceArea}
          isMemberListLoading={isLoadingGetServiceAreaById}
          serviceAreaName={serviceAreaState.selectedServiceAreaName}
          handleBackClick={handleServiceAreaAssigneeModalBack}
        />
      )}
    </div>
  );
};

export default CustomerDetailSection;
